import React, { useEffect, useRef, useState } from 'react';
import { mediaApi } from '../api/media';
import { getErrorMessage } from '../api/client';
import Spinner from './ui/Spinner';
import Alert from './ui/Alert';
import { IconMusic, IconTrash, IconDownload } from './ui/Icons';

// Necesitas IconMusic e IconDownload — agrégalos a Icons.js si no existen

export default function AudioUploader({ eventId }) {
  const [audio, setAudio] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState('');
  const inputRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const data = await mediaApi.getAudio(eventId);
      setAudio(data.audio);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [eventId]);

  const handleFile = async (file) => {
    if (!file) return;
    setError('');
    setUploading(true);
    setProgress(0);
    try {
      const media = await mediaApi.uploadAudio(eventId, file, setProgress);
      setAudio(media);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('¿Eliminar la música de fondo?')) return;
    try {
      await mediaApi.deleteAudio(eventId);
      setAudio(null);
    } catch (err) {
      setError(getErrorMessage(err));
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-semibold text-slate-800">Música de fondo</h3>
          <p className="text-xs text-slate-500">
            Se reproducirá automáticamente cuando tus invitados abran la invitación.
          </p>
        </div>
      </div>

      {error && <Alert type="error">{error}</Alert>}

      {loading ? (
        <div className="grid place-items-center py-6">
          <Spinner className="text-slate-400" />
        </div>
      ) : audio ? (
        <div className="border border-slate-200 rounded-xl p-4 space-y-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-slate-900 text-white grid place-items-center flex-shrink-0">
              <span className="block w-5 h-5">
                <IconMusic className="w-full h-full" />
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-slate-800 truncate">Música cargada</p>
              <p className="text-xs text-slate-500">Se reproduce en la invitación pública</p>
            </div>
            <button
              type="button"
              onClick={handleDelete}
              aria-label="Eliminar música"
              className="w-9 h-9 grid place-items-center rounded-lg text-slate-400 hover:text-red-600 hover:bg-red-50 transition-colors"
            >
              <span className="block w-4 h-4">
                <IconTrash className="w-full h-full" />
              </span>
            </button>
          </div>
          <audio controls src={audio.url} className="w-full h-10" preload="metadata">
            Tu navegador no soporta audio.
          </audio>
        </div>
      ) : (
        <label className="block border-2 border-dashed border-slate-300 rounded-xl p-6 text-center hover:border-slate-900 hover:bg-slate-50 transition-colors cursor-pointer">
          <input
            ref={inputRef}
            type="file"
            accept="audio/mpeg,audio/mp4,audio/wav,audio/ogg,audio/webm"
            className="hidden"
            onChange={(e) => handleFile(e.target.files?.[0])}
          />
          {uploading ? (
            <div className="space-y-2">
              <Spinner size="sm" className="text-slate-400 mx-auto" />
              <p className="text-xs text-slate-500">Subiendo {progress}%</p>
            </div>
          ) : (
            <>
              <div className="w-10 h-10 mx-auto mb-3 rounded-lg bg-slate-100 grid place-items-center text-slate-500">
                <span className="block w-5 h-5">
                  <IconMusic className="w-full h-full" />
                </span>
              </div>
              <p className="text-sm font-semibold text-slate-700">Subir música</p>
              <p className="text-xs text-slate-500 mt-1">MP3, M4A, WAV u OGG · máx 15 MB</p>
            </>
          )}
        </label>
      )}
    </div>
  );
}